// src/lib/efhSnapshot.ts
// EFH-Snapshot, gleiches Muster wie src/lib/etwSnapshot.ts (siehe dort fuer die
// ausfuehrliche Begruendung, insbesondere warum diese Datei absichtlich keine
// anderen src/lib-Dateien importiert). EFH hat ein anderes Eingabe-Schema als
// ETW (Mietausfall/Instandhaltung als absolute Jahresbetraege statt Prozentsaetze,
// kein CapRate/Wert-ueber-NOI) -- bewusst nicht an ETW angeglichen, sondern 1:1
// das bestehende EFH-Schema.
//
// Reparatur-Hinweis: Der bisherige EFH-JSON-Export schrieb unter "input" (siehe
// handleExportJSON), der bisherige Import las aber vom Top-Level des geparsten
// Objekts (siehe importJson: "d.kaufpreis" statt "d.input.kaufpreis") -- der
// Rundweg war dadurch bereits kaputt, unabhaengig von diesem Umbau. Der Export
// schrieb ausserdem den BEREITS angepassten Preis (KP, inkl. priceAdjPct) als
// "kaufpreis", obwohl priceAdjPct zusaetzlich mitexportiert wurde -- bei
// korrektem Import waere die Anpassung doppelt angewendet worden. Diese Datei
// behebt beides: kaufpreis ist immer der rohe Basispreis, die Anpassung lebt
// ausschliesslich in playground.priceAdjPct.
//
// Finanzierung seit 14.10.2026 Runde 2 (DEAL-FLOW-STATUS.md "Als Naechstes nach
// Runde 1"): Zins + anfaengliche Tilgung (tilgungPct) statt Zins + Laufzeit
// (laufzeitYears) -- wie ETW/MFH. Alte gespeicherte Deals/Exporte haben noch
// "laufzeitYears" statt "tilgungPct" im JSON -- resolveEfhSnapshot() rechnet das
// beim Laden einmalig um (tilgungPctFromLaufzeit() unten), damit sie weiterhin
// denselben Jahresbetrag ergeben. Ein spaeteres Speichern schreibt dann bereits
// das neue Feld.

export type AnalyzerTypeId = "etw" | "mfh" | "efh" | "gewerbe" | "mixeduse";

/** confirmedFields: Phase-4 Runde 2b, Baustein 2 -- siehe etwSnapshot.ts fuer die
 *  ausfuehrliche Begruendung. Optional/abwaertskompatibel. */
export type DealSnapshotEnvelope<TInput, TPlayground> = {
  version: number;
  analyzerType: AnalyzerTypeId;
  input: TInput;
  playground: TPlayground;
  confirmedFields: string[];
};

function resolveField<T>(raw: Record<string, unknown> | undefined, key: string, fallback: T): T {
  const value = raw?.[key];
  if (value === undefined || value === null) return fallback;
  if (typeof fallback === "number") return typeof value === "number" && Number.isFinite(value) ? (value as T) : fallback;
  if (typeof fallback === "boolean") return typeof value === "boolean" ? (value as T) : fallback;
  if (typeof fallback === "string") return typeof value === "string" ? (value as T) : fallback;
  return (value as T) ?? fallback;
}

function asRecord(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" ? (value as Record<string, unknown>) : {};
}

function resolveConfirmedFields(raw: Record<string, unknown> | undefined, key: string): string[] {
  const value = raw?.[key];
  if (!Array.isArray(value)) return [];
  return value.filter((v): v is string => typeof v === "string");
}

export const EFH_SNAPSHOT_VERSION = 1;

export type EfhSnapshotInput = {
  kaufpreis: number;
  jahreskaltmiete: number;
  mietausfallPct: number;
  nichtUmlagefaehigJahr: number;
  instandhaltungJahr: number;
  // Bewirtschaftung Pauschal/Einzeln (Baustein 5): identisches Muster wie MfhSnapshotInput
  // (src/lib/mfhSnapshot.ts) -- null = kein Modus gespeichert, Migrationsentscheidung faellt
  // in efhDealScore.ts' resolveEfhBewirtschaftung(). flaecheM2 ist neu (EFH hatte bisher kein
  // Flaechenfeld) -- nur fuer den § 28 II. BV-Instandhaltungsvorschlag gebraucht.
  bewirtschaftungModus: "pauschal" | "einzeln" | null;
  verwaltung: number;
  instandhaltungJeM2Jahr: number;
  flaecheM2: number;
  baujahr: number | null;
  hatAufzug: boolean;
  financingOn: boolean;
  /** Vereinheitlichte Darlehensregel (16.10.2026, Dominiks Vorgabe): Eigenkapital in Euro
   *  statt Beleihungsquote (LTV) -- wie MFH. Kredit = Kaufpreis − max(0, EK − Nebenkosten). */
  eigenkapital: number;
  /** Migrations-Bruecke: bei einem alten Snapshot/Export (vor der Umstellung auf
   *  eigenkapital) der frueher gespeicherte ltvPct-Wert, sonst null. Nebenkosten sind in
   *  dieser Datei bewusst nicht verfuegbar (eigenes Subsystem, s. Dateikommentar) -- die
   *  eigentliche Umrechnung in ein aequivalentes Eigenkapital passiert deshalb erst dort,
   *  wo Nebenkosten bereits aufgeloest sind (efhDealScore.ts' resolveEfhEigenkapital()). */
  legacyLtvPct: number | null;
  zinsPct: number;
  tilgungPct: number;
  mietSteigerung: number;
  kostenSteigerung: number;
  /** Deal-Score v2 Leerstand-Obergrenze. Alte Snapshots ohne dieses Feld gelten als "vollstaendig". */
  vermietet: "vollstaendig" | "teilweise" | "nein";
};

export type EfhSnapshotPlayground = {
  priceAdjPct: number;
  rentAdjPct: number;
  applyAdjustments: boolean;
};

export type EfhSnapshot = DealSnapshotEnvelope<EfhSnapshotInput, EfhSnapshotPlayground>;

// UI-Defaults, siehe EinfamilienhausCheck.tsx (Beispielobjekt).
export const EFH_INPUT_DEFAULTS: EfhSnapshotInput = {
  kaufpreis: 550_000,
  jahreskaltmiete: 30_500,
  mietausfallPct: 0.05,
  nichtUmlagefaehigJahr: 2_000,
  instandhaltungJahr: 3_000,
  bewirtschaftungModus: null,
  verwaltung: 0,
  instandhaltungJeM2Jahr: 0,
  flaecheM2: 140,
  baujahr: null,
  hatAufzug: false,
  financingOn: true,
  eigenkapital: 330_000,
  legacyLtvPct: null,
  zinsPct: 0.042,
  tilgungPct: 0.02,
  mietSteigerung: 0.01,
  kostenSteigerung: 0.015,
  vermietet: "vollstaendig",
};

export const EFH_PLAYGROUND_DEFAULTS: EfhSnapshotPlayground = {
  priceAdjPct: 0,
  rentAdjPct: 0,
  applyAdjustments: true,
};

export function buildEfhSnapshot(
  input: EfhSnapshotInput,
  playground: EfhSnapshotPlayground,
  confirmedFields: string[] = []
): EfhSnapshot {
  return { version: EFH_SNAPSHOT_VERSION, analyzerType: "efh", input, playground, confirmedFields };
}

export type ResolvedEfhSnapshot = {
  input: EfhSnapshotInput;
  playground: EfhSnapshotPlayground;
  /** false, wenn die Kern-Eingaben (Kaufpreis/Jahreskaltmiete) fehlen -- Altzeile oder leerer Import. */
  complete: boolean;
  confirmedFields: string[];
};

/**
 * Migration altgespeicherter Deals (vor 14.10.2026 Runde 2): aus Zins + Laufzeit die
 * dazu aequivalente anfaengliche Tilgungsrate ableiten, damit ein alter, mit Laufzeit
 * gespeicherter EFH-Deal beim Laden denselben Jahresbetrag ergibt wie vorher. Herleitung:
 * die exakt amortisierende Annuitaet ist Rate = Darlehen x Zins/(1-(1+Zins)^-Laufzeit) --
 * das Verhaeltnis Rate/Darlehen ist darlehens-unabhaengig, Tilgung = Rate/Darlehen - Zins
 * vereinfacht algebraisch zu Zins/((1+Zins)^Laufzeit - 1).
 */
export function tilgungPctFromLaufzeit(zinsPct: number, laufzeitYears: number): number {
  if (zinsPct <= 0) return laufzeitYears > 0 ? 1 / laufzeitYears : 0;
  const denom = Math.pow(1 + zinsPct, laufzeitYears) - 1;
  return denom > 0 ? zinsPct / denom : 0;
}

/** tilgungPct (neues Format) direkt uebernehmen, sonst aus laufzeitYears (altes Format,
 *  vor 14.10.2026 Runde 2) umrechnen, sonst der Default. */
function resolveTilgungPct(rawInput: Record<string, unknown> | undefined, zinsPctResolved: number): number {
  const direct = rawInput?.tilgungPct;
  if (typeof direct === "number" && Number.isFinite(direct)) return direct;
  const laufzeitYears = rawInput?.laufzeitYears;
  if (typeof laufzeitYears === "number" && Number.isFinite(laufzeitYears) && laufzeitYears > 0) {
    return tilgungPctFromLaufzeit(zinsPctResolved, laufzeitYears);
  }
  return EFH_INPUT_DEFAULTS.tilgungPct;
}

export function resolveEfhSnapshot(raw: unknown): ResolvedEfhSnapshot {
  const r = asRecord(raw);
  const rawInput = asRecord(r.input);
  const rawPlayground = asRecord(r.playground);

  const complete =
    typeof rawInput.kaufpreis === "number" &&
    typeof rawInput.jahreskaltmiete === "number";

  const zinsPct = resolveField(rawInput, "zinsPct", EFH_INPUT_DEFAULTS.zinsPct);
  const input: EfhSnapshotInput = {
    kaufpreis: resolveField(rawInput, "kaufpreis", EFH_INPUT_DEFAULTS.kaufpreis),
    jahreskaltmiete: resolveField(rawInput, "jahreskaltmiete", EFH_INPUT_DEFAULTS.jahreskaltmiete),
    mietausfallPct: resolveField(rawInput, "mietausfallPct", EFH_INPUT_DEFAULTS.mietausfallPct),
    nichtUmlagefaehigJahr: resolveField(rawInput, "nichtUmlagefaehigJahr", EFH_INPUT_DEFAULTS.nichtUmlagefaehigJahr),
    instandhaltungJahr: resolveField(rawInput, "instandhaltungJahr", EFH_INPUT_DEFAULTS.instandhaltungJahr),
    bewirtschaftungModus: rawInput.bewirtschaftungModus === "pauschal" || rawInput.bewirtschaftungModus === "einzeln" ? rawInput.bewirtschaftungModus : null,
    verwaltung: resolveField(rawInput, "verwaltung", EFH_INPUT_DEFAULTS.verwaltung),
    instandhaltungJeM2Jahr: resolveField(rawInput, "instandhaltungJeM2Jahr", EFH_INPUT_DEFAULTS.instandhaltungJeM2Jahr),
    flaecheM2: resolveField(rawInput, "flaecheM2", EFH_INPUT_DEFAULTS.flaecheM2),
    baujahr: typeof rawInput.baujahr === "number" && Number.isFinite(rawInput.baujahr) ? rawInput.baujahr : null,
    hatAufzug: resolveField(rawInput, "hatAufzug", EFH_INPUT_DEFAULTS.hatAufzug),
    financingOn: resolveField(rawInput, "financingOn", EFH_INPUT_DEFAULTS.financingOn),
    eigenkapital: resolveField(rawInput, "eigenkapital", EFH_INPUT_DEFAULTS.eigenkapital),
    legacyLtvPct:
      (rawInput?.eigenkapital === undefined || rawInput?.eigenkapital === null) &&
      typeof rawInput?.ltvPct === "number" && Number.isFinite(rawInput.ltvPct)
        ? (rawInput.ltvPct as number)
        : null,
    zinsPct,
    tilgungPct: resolveTilgungPct(rawInput, zinsPct),
    mietSteigerung: resolveField(rawInput, "mietSteigerung", EFH_INPUT_DEFAULTS.mietSteigerung),
    kostenSteigerung: resolveField(rawInput, "kostenSteigerung", EFH_INPUT_DEFAULTS.kostenSteigerung),
    vermietet:
      rawInput.vermietet === "teilweise" || rawInput.vermietet === "nein"
        ? rawInput.vermietet
        : "vollstaendig",
  };

  const playground: EfhSnapshotPlayground = {
    priceAdjPct: resolveField(rawPlayground, "priceAdjPct", resolveField(rawInput, "priceAdjPct", EFH_PLAYGROUND_DEFAULTS.priceAdjPct)),
    rentAdjPct: resolveField(rawPlayground, "rentAdjPct", resolveField(rawInput, "rentAdjPct", EFH_PLAYGROUND_DEFAULTS.rentAdjPct)),
    applyAdjustments: resolveField(rawPlayground, "applyAdjustments", resolveField(rawInput, "applyAdjustments", EFH_PLAYGROUND_DEFAULTS.applyAdjustments)),
  };

  const confirmedFields = resolveConfirmedFields(r, "confirmedFields");

  return { input, playground, complete, confirmedFields };
}
